import React, { useCallback, useState } from 'react';
import { Package, Trash2, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { ScanInput } from '../../../common/scanning/ScanInput';
import { procurementService } from '../../../../services/procurementService';
import type {
  Department,
  ProcurementItemLookup,
  ProcurementOrderLineDraft,
} from '../../../../types/procurement';
import { ReplenishmentModal } from './ReplenishmentModal';

interface OrderLinesStepProps {
  legalEntityNo: string;
  legalEntityName: string;
  department: Department;
  operatorName: string;
  lines: ProcurementOrderLineDraft[];
  onLinesChange: (lines: ProcurementOrderLineDraft[]) => void;
  onFinish: () => void;
  onCancel: () => void;
  submitting: boolean;
}

export const OrderLinesStep: React.FC<OrderLinesStepProps> = ({
  legalEntityNo,
  legalEntityName,
  department,
  operatorName,
  lines,
  onLinesChange,
  onFinish,
  onCancel,
  submitting,
}) => {
  const [scanValue, setScanValue] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [pendingLookup, setPendingLookup] = useState<ProcurementItemLookup | null>(null);

  const appendLine = (line: ProcurementOrderLineDraft) => {
    onLinesChange([...lines, line]);
  };

  const removeLine = (index: number) => {
    onLinesChange(lines.filter((_, i) => i !== index));
  };

  const updateLineQuantity = (index: number, newQty: number) => {
    onLinesChange(
      lines.map((line, i) => (i === index ? { ...line, quantity: newQty } : line))
    );
  };

  const processCode = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) return;

      setLookupError(null);
      setLookupLoading(true);
      try {
        const match = await procurementService.lookupItem(
          legalEntityNo,
          department.departmentNo,
          trimmed
        );

        if (!match) {
          setLookupError(`Vare "${trimmed}" er ikke definert som bestillingsvare for denne avdelingen.`);
          return;
        }

        const recommended = match.recommendedQuantity ?? 0;
        const balance = match.warehouseBalance ?? 0;

        if (balance >= recommended && recommended > 0) {
          // Golden path - auto-add
          appendLine({
            itemNo: match.procurementItemNo,
            description: match.procurementItemDescription,
            quantity: recommended,
            unit: match.recommendedUnit,
          });
          setScanValue('');
        } else {
          // Low balance - show modal
          setPendingLookup(match);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ukjent feil';
        setLookupError(`Kunne ikke slå opp vare: ${message}`);
      } finally {
        setLookupLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [legalEntityNo, department.departmentNo, lines]
  );

  const handleKeepOriginal = () => {
    if (!pendingLookup) return;
    appendLine({
      itemNo: pendingLookup.procurementItemNo,
      description: pendingLookup.procurementItemDescription,
      quantity: pendingLookup.recommendedQuantity ?? 0,
      unit: pendingLookup.recommendedUnit,
    });
    setPendingLookup(null);
    setScanValue('');
  };

  const handleCancelLine = () => {
    setPendingLookup(null);
    setScanValue('');
  };

  const handleSwitchToReplenishment = () => {
    if (!pendingLookup || !pendingLookup.replenishmentItemNo) return;
    appendLine({
      itemNo: pendingLookup.replenishmentItemNo,
      description: pendingLookup.replenishmentDescription,
      quantity: pendingLookup.recommendedQuantityReplenishmentItem ?? 0,
      unit: pendingLookup.recommendedUnitReplenishmentItem,
    });
    setPendingLookup(null);
    setScanValue('');
  };

  return (
    <div className="space-y-4">
      {/* Compact header summary */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-sm flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <span className="text-gray-500">Selskap:</span>{' '}
          <strong>
            {legalEntityNo} – {legalEntityName}
          </strong>
        </div>
        <div>
          <span className="text-gray-500">Avdeling:</span>{' '}
          <strong>
            {department.departmentNo} – {department.departmentName}
          </strong>
        </div>
        <div>
          <span className="text-gray-500">Bestiller:</span> <strong>{operatorName}</strong>
        </div>
      </div>

      {/* Scan area */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Package className="text-blue-600" size={20} />
          <h2 className="text-lg font-semibold text-gray-800">Legg til varelinje</h2>
        </div>
        <ScanInput
          value={scanValue}
          onChange={setScanValue}
          onScan={processCode}
          placeholder="Skann eller tast varenummer / GTIN"
          autoFocus
          loading={lookupLoading}
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => processCode(scanValue)}
            disabled={lookupLoading || !scanValue.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded flex items-center gap-2"
          >
            {lookupLoading ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
            Legg til
          </button>
          {lookupError && (
            <div className="text-sm text-red-700 flex items-center gap-1.5">
              <AlertCircle size={16} />
              {lookupError}
            </div>
          )}
        </div>
      </div>

      {/* Lines table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            Bestillingslinjer{' '}
            <span className="text-gray-500 font-normal">({lines.length})</span>
          </h3>
        </div>
        {lines.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            Ingen linjer lagt til ennå. Skann eller tast et varenummer over.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">#</th>
                <th className="px-4 py-2 text-left">Varenummer</th>
                <th className="px-4 py-2 text-left">Beskrivelse</th>
                <th className="px-4 py-2 text-right">Antall</th>
                <th className="px-4 py-2 text-left">Enhet</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.itemNo}-${i}`} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                  <td className="px-4 py-2 font-medium">{l.itemNo}</td>
                  <td className="px-4 py-2 text-gray-700">{l.description || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <input
                      type="number"
                      step="any"
                      min={0}
                      value={l.quantity}
                      onChange={(e) => {
                        const v = e.target.value === '' ? 0 : Number(e.target.value);
                        if (!Number.isNaN(v)) updateLineQuantity(i, v);
                      }}
                      onFocus={(e) => e.target.select()}
                      className="w-24 text-right border border-gray-300 rounded px-2 py-1 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      aria-label="Antall"
                    />
                  </td>
                  <td className="px-4 py-2 text-gray-700">{l.unit || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => removeLine(i)}
                      className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                      aria-label="Fjern linje"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="text-gray-700 hover:text-gray-900 px-4 py-2 font-medium"
        >
          Avbryt hele bestillingen
        </button>
        <button
          onClick={onFinish}
          disabled={lines.length === 0 || submitting}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg flex items-center gap-2 shadow"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
          Avslutt bestilling ({lines.length})
        </button>
      </div>

      {pendingLookup && (
        <ReplenishmentModal
          lookup={pendingLookup}
          onKeepOriginal={handleKeepOriginal}
          onCancelLine={handleCancelLine}
          onSwitchToReplenishment={handleSwitchToReplenishment}
          onClose={handleCancelLine}
        />
      )}
    </div>
  );
};
